import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, ListAlt, Webhook } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Company, companiesService } from '../services/firebaseFunctions';
import { useCompanyGroups, useCompanyPagination, useNetworks } from '../hooks';
import LogoUpload from '../components/LogoUpload';
import { formatFirestoreDateOnly } from '../utils/dateUtils';
import { cnpjDigits, formatCnpj, formatCnpjInput, isValidCnpj } from '../utils/formatCnpj';
import { companyGroupErrorMessage, operationErrorMessage } from '../utils/adminFeedback';
import { useAuth } from '../contexts/AuthContext';
import DestructiveActionDialog from '../components/DestructiveActionDialog';

type Form = {
  name: string;
  cnpj: string;
  isActive: boolean;
  activity: string;
  logoUrl: string;
  addToReedyFiscal: boolean;
  networkId: string;
  companyGroupId: string;
};

const emptyForm: Form = {
  name: '',
  cnpj: '',
  isActive: true,
  activity: '',
  logoUrl: '',
  addToReedyFiscal: false,
  networkId: '',
  companyGroupId: '',
};

const CompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('empresas', 'editar');
  const { networks } = useNetworks();
  const { companyGroups } = useCompanyGroups();
  const page = useCompanyPagination(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [notice, setNotice] = useState<string>();
  const [noticeSeverity, setNoticeSeverity] = useState<'success' | 'error'>('error');
  const [saving, setSaving] = useState(false);
  const [busyCompanyId, setBusyCompanyId] = useState<string>();
  const [companyPendingDeletion, setCompanyPendingDeletion] = useState<Company | null>(null);
  const [cnpjError, setCnpjError] = useState<string>();
  const [nameError, setNameError] = useState<string>();

  const showNotice = (message: string, severity: 'success' | 'error') => {
    setNotice(message);
    setNoticeSeverity(severity);
  };

  const openDialog = (company?: Company) => {
    setEditingCompany(company || null);
    setCnpjError(undefined);
    setNameError(undefined);
    setForm(company ? {
      name: company.name,
      cnpj: company.cnpj,
      isActive: company.isActive,
      activity: company.activity || '',
      logoUrl: (company as Company & { logoUrl?: string }).logoUrl || '',
      addToReedyFiscal: false,
      networkId: company.networkId || '',
      companyGroupId: company.companyGroupId || '',
    } : emptyForm);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!saving) setDialogOpen(false);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) {
      setNameError('Informe o nome da Empresa.');
      return;
    }
    setNameError(undefined);
    const canonicalCnpj = cnpjDigits(form.cnpj);
    if (!editingCompany && !isValidCnpj(canonicalCnpj)) {
      setCnpjError('Informe um CNPJ válido.');
      return;
    }
    setCnpjError(undefined);
    const commonPayload = {
      name,
      isActive: form.isActive,
      addToReedyFiscal: form.addToReedyFiscal,
    };

    try {
      setSaving(true);
      if (editingCompany) {
        // CNPJ é imutável no contrato de update e não deve ser enviado ao backend.
        const result = await companiesService.update(editingCompany.id, {
          ...commonPayload,
          // Em edição, vazio remove o dado opcional já persistido.
          activity: form.activity.trim() || null,
          logoUrl: form.logoUrl.trim() || null,
        });
        if (!result.success) {
          showNotice(operationErrorMessage('company', result), 'error');
          return;
        }

        if ((editingCompany.networkId || null) !== (form.networkId || null)) {
          await companiesService.setNetwork(
            editingCompany.id,
            form.networkId || null,
            editingCompany.networkId || null,
          );
        }
        if ((editingCompany.companyGroupId || null) !== (form.companyGroupId || null)) {
          try {
            await companiesService.setCompanyGroup(
              editingCompany.id,
              form.companyGroupId || null,
              editingCompany.companyGroupId || null,
            );
          } catch (groupError) {
            setDialogOpen(false);
            showNotice(companyGroupErrorMessage(groupError), 'error');
            await page.refresh();
            return;
          }
        }
        showNotice('Empresa atualizada com sucesso!', 'success');
      } else {
        const result = await companiesService.create({
          ...commonPayload,
          // Na criação, explicitar ausência mantém compatibilidade com clientes
          // legados. A callable normaliza `null` e nunca o persiste.
          activity: form.activity.trim() || null,
          logoUrl: form.logoUrl.trim() || null,
          cnpj: canonicalCnpj,
          networkId: form.networkId || null,
          companyGroupId: form.companyGroupId || null,
        });
        if (!result.success) {
          showNotice(operationErrorMessage('company', result), 'error');
          return;
        }
        showNotice('Empresa criada com sucesso!', 'success');
      }

      setDialogOpen(false);
      await page.refresh();
    } catch (error) {
      const message = error instanceof Error && error.message.includes('aborted')
        ? 'O vínculo foi alterado por outra sessão; a listagem foi recarregada.'
        : 'Não foi possível salvar a Empresa.';
      showNotice(message, 'error');
      await page.refresh();
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!companyPendingDeletion) return;
    try {
      setBusyCompanyId(companyPendingDeletion.id);
      const result = await companiesService.delete(companyPendingDeletion.id);
      if (!result.success) {
        showNotice(operationErrorMessage('company', result), 'error');
        return;
      }
      await page.refresh();
      showNotice('Empresa excluída com sucesso!', 'success');
      setCompanyPendingDeletion(null);
    } catch {
      showNotice(operationErrorMessage('company'), 'error');
    } finally {
      setBusyCompanyId(undefined);
    }
  };

  const toggle = async (company: Company) => {
    try {
      setBusyCompanyId(company.id);
      const result = await companiesService.toggleActive(company.id);
      if (!result.success) throw new Error(result.error);
      await page.refresh();
      showNotice(result.message || 'Status alterado com sucesso!', 'success');
    } catch {
      showNotice('Não foi possível alterar o status.', 'error');
    } finally {
      setBusyCompanyId(undefined);
    }
  };

  return <Box>
    <Typography variant="h4" sx={{ mb: 3 }}>Empresas</Typography>
    {page.error && <Alert severity="error" sx={{ mb: 2 }}>{page.error}</Alert>}
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      <TextField size="small" label="Filtrar por nome" value={page.search} onChange={(event) => page.setSearch(event.target.value)} />
      <Select size="small" value={page.networkId || ''} displayEmpty onChange={(event) => page.setNetworkId(event.target.value || undefined)}>
        <MenuItem value="">Todas as redes</MenuItem>
        {networks.map((network) => <MenuItem key={network.id} value={network.id}>{network.name}</MenuItem>)}
      </Select>
      <Select size="small" value={page.companyGroupId || ''} displayEmpty inputProps={{ 'aria-label': 'Filtrar por Grupo' }} onChange={(event) => page.setCompanyGroupId(event.target.value || undefined)}>
        <MenuItem value="">Todos os grupos</MenuItem>
        {companyGroups.map((group) => <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>)}
      </Select>
      <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Empresas.'}>
        <span>
          <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()} disabled={!canManage}>Nova Empresa</Button>
        </span>
      </Tooltip>
    </Box>
    <Paper>
      <Table>
        <TableHead><TableRow>
          <TableCell>Nome</TableCell><TableCell>CNPJ</TableCell><TableCell>Atividade</TableCell><TableCell>Rede</TableCell><TableCell>Grupo</TableCell>
          <TableCell>Status</TableCell><TableCell>Listas (mapa)</TableCell><TableCell>Criado em</TableCell><TableCell align="right">Ações</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {page.loading ? <TableRow><TableCell colSpan={9}><CircularProgress size={18} /></TableCell></TableRow>
            : page.items.length === 0 ? <TableRow><TableCell colSpan={9}>Nenhuma Empresa encontrada.</TableCell></TableRow>
              : page.items.map((company) => {
                const busy = busyCompanyId === company.id;
                const listMap = company.lists || {};
                const productCount = Object.keys(listMap).length;
                const listCount = Object.values(listMap).reduce((total, lists) => total + (lists?.length || 0), 0);
                return <TableRow key={company.id}>
                  <TableCell>{company.name}</TableCell><TableCell>{formatCnpj(company.cnpj)}</TableCell><TableCell>{company.activity || '-'}</TableCell>
                  <TableCell>{company.network?.name || 'Sem rede'}</TableCell>
                  <TableCell>{company.companyGroup?.name || 'Sem grupo'}</TableCell>
                  <TableCell><Chip size="small" label={company.isActive ? 'Ativa' : 'Inativa'} color={company.isActive ? 'success' : 'default'} /></TableCell>
                  <TableCell>{productCount ? <Chip label={`${productCount} ativo(s) · ${listCount} lista(s)`} size="small" variant="outlined" color="info" /> : '—'}</TableCell>
                  <TableCell>{formatFirestoreDateOnly(company.createdAt)}</TableCell>
                  <TableCell align="right">
                    {company.evoWebhookConfigId && <Tooltip title="Abrir configuração webhook EVO"><IconButton aria-label="Abrir webhook" disabled={busy} onClick={() => navigate(`/evo-webhooks/${company.evoWebhookConfigId}`)}><Webhook /></IconButton></Tooltip>}
                    <Tooltip title="Gerenciar listas"><IconButton aria-label="Gerenciar listas" disabled={busy} onClick={() => navigate(`/companies/${company.id}/lists`)}><ListAlt /></IconButton></Tooltip>
                    <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Empresas.'}>
                      <span>
                        <IconButton aria-label="Editar empresa" disabled={!canManage || busy} onClick={() => openDialog(company)}><Edit /></IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Empresas.'}>
                      <span>
                        <Button size="small" disabled={!canManage || busy} onClick={() => void toggle(company)}>{company.isActive ? 'Desativar' : 'Ativar'}</Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Empresas.'}>
                      <span>
                        <IconButton aria-label="Excluir empresa" color="error" disabled={!canManage || busy} onClick={() => setCompanyPendingDeletion(company)}><Delete /></IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>;
              })}
        </TableBody>
      </Table>
    </Paper>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
      <Typography>{page.totalCount} Empresa(s)</Typography>
      <Box><Button disabled={page.page === 0 || page.loading} onClick={page.previous}>Anterior</Button><Button disabled={!page.nextCursor || page.loading} onClick={page.next}>Próxima</Button></Box>
    </Box>
    <Dialog open={dialogOpen} onClose={closeDialog} fullWidth>
      <DialogTitle>{editingCompany ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          required
          margin="normal"
          label="Nome da Empresa"
          disabled={saving}
          value={form.name}
          error={Boolean(nameError)}
          helperText={nameError}
          onChange={(event) => {
            setNameError(undefined);
            setForm({ ...form, name: event.target.value });
          }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="CNPJ"
          disabled={Boolean(editingCompany) || saving}
          value={editingCompany ? formatCnpj(form.cnpj) : form.cnpj}
          error={Boolean(cnpjError)}
          helperText={cnpjError}
          onChange={(event) => {
            setCnpjError(undefined);
            setForm({ ...form, cnpj: formatCnpjInput(event.target.value) });
          }}
        />
        <TextField fullWidth margin="normal" label="Atividade" disabled={saving} value={form.activity} onChange={(event) => setForm({ ...form, activity: event.target.value })} />
        <Select fullWidth disabled={saving} value={form.networkId} displayEmpty onChange={(event) => setForm({ ...form, networkId: event.target.value })}>
          <MenuItem value="">Sem rede</MenuItem>{networks.map((network) => <MenuItem key={network.id} value={network.id}>{network.name}</MenuItem>)}
        </Select>
        <Select fullWidth disabled={saving} value={form.companyGroupId} displayEmpty inputProps={{ 'aria-label': 'Grupo' }} sx={{ mt: 2 }} onChange={(event) => setForm({ ...form, companyGroupId: event.target.value })}>
          <MenuItem value="">Sem grupo</MenuItem>{companyGroups.map((group) => <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>)}
        </Select>
        <LogoUpload currentLogoUrl={form.logoUrl} disabled={saving} onLogoChange={(url) => setForm({ ...form, logoUrl: url || '' })} />
        <FormControlLabel control={<Switch checked={form.isActive} disabled={saving} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />} label="Empresa ativa" />
        <FormControlLabel control={<Checkbox checked={form.addToReedyFiscal} disabled={saving} onChange={(event) => setForm({ ...form, addToReedyFiscal: event.target.checked })} />} label="Adicionar empresa ao Reedy Fiscal" />
      </DialogContent>
      <DialogActions><Button disabled={saving} onClick={closeDialog}>Cancelar</Button><Button variant="contained" disabled={saving} onClick={() => void save()}>Salvar</Button></DialogActions>
    </Dialog>
    <DestructiveActionDialog
      open={Boolean(companyPendingDeletion)}
      entityLabel="Empresa"
      entityName={companyPendingDeletion?.name || ''}
      busy={Boolean(companyPendingDeletion && busyCompanyId === companyPendingDeletion.id)}
      onCancel={() => setCompanyPendingDeletion(null)}
      onConfirm={() => void remove()}
    />
    <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(undefined)}><Alert severity={noticeSeverity} onClose={() => setNotice(undefined)}>{notice}</Alert></Snackbar>
  </Box>;
};

export default CompaniesPage;
